import React, { useState } from "react";
import { Edit, Trash } from "lucide-react";

interface ProductSaltTabProps {
  salts: { id: string; name: string }[];
  onChange: (salts: { id: string; name: string }[]) => void;
  isEdit: boolean;
  onUpdate?: () => void;
  isLoading?: boolean;
}

const formStyles = {
  lableStyle: "block text-sm text-[#3F5260] font-medium mb-1",
  inputStyle:
    "w-full px-3 py-2 border border-[#D0D5DE] outline-none rounded-lg focus:ring-1 focus:ring-emerald-500 text-sm",
};

export const ProductSaltTab: React.FC<ProductSaltTabProps> = ({
  salts,
  onChange,
  isEdit,
  onUpdate,
  isLoading,
}) => {
  const [newSaltName, setNewSaltName] = useState("");
  const [editingSaltId, setEditingSaltId] = useState<string | null>(null);
  const [editingSaltName, setEditingSaltName] = useState("");

  const handleAddSalt = () => {
    if (!newSaltName.trim()) return;
    const newSalt = {
      id: Date.now().toString(),
      name: newSaltName.trim(),
    };
    onChange([...salts, newSalt]);
    setNewSaltName("");
  };

  const handleDeleteSalt = (id: string) => {
    onChange(salts.filter((s) => s.id !== id));
  };

  const handleSaveEdit = () => {
    if (!editingSaltId || !editingSaltName.trim()) return;
    onChange(
      salts.map((s) =>
        s.id === editingSaltId ? { ...s, name: editingSaltName.trim() } : s
      )
    );
    setEditingSaltId(null);
    setEditingSaltName("");
  };

  return (
    <div className="space-y-6">
      <div className="flex gap-4 items-end">
        <div className="flex-1">
          <label className={formStyles.lableStyle}>Add New Salt</label>
          <input
            type="text"
            value={newSaltName}
            onChange={(e) => setNewSaltName(e.target.value)}
            className={formStyles.inputStyle}
            placeholder="Enter salt name"
          />
        </div>
        <button
          type="button"
          onClick={handleAddSalt}
          className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition"
        >
          Add
        </button>
      </div>

      <div className="mt-4 border rounded-lg overflow-hidden">
        <table className="w-full text-sm text-left text-gray-500">
          <thead className="text-xs text-gray-700 uppercase bg-gray-50">
            <tr>
              <th className="px-6 py-3">Salt Name</th>
              <th className="px-6 py-3 w-32">Actions</th>
            </tr>
          </thead>
          <tbody>
            {salts.map((salt) => (
              <tr key={salt.id} className="bg-white border-b hover:bg-gray-50">
                <td className="px-6 py-4 font-medium text-gray-900">
                  {editingSaltId === salt.id ? (
                    <input
                      type="text"
                      value={editingSaltName}
                      onChange={(e) => setEditingSaltName(e.target.value)}
                      className={formStyles.inputStyle}
                    />
                  ) : (
                    salt.name
                  )}
                </td>
                <td className="px-6 py-4">
                  {editingSaltId === salt.id ? (
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleSaveEdit}
                        className="text-emerald-600 hover:text-emerald-800 font-medium"
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingSaltId(null)}
                        className="text-gray-500 hover:text-gray-700 font-medium"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => {
                          setEditingSaltId(salt.id);
                          setEditingSaltName(salt.name);
                        }}
                        className="text-blue-500 hover:text-blue-700"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteSalt(salt.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {salts.length === 0 && (
              <tr>
                <td colSpan={2} className="px-6 py-4 text-center text-gray-500">
                  No salts added yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {isEdit && (
        <div className="flex justify-end mt-6">
          <button
            type="button"
            onClick={onUpdate}
            disabled={isLoading}
            className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition"
          >
            {isLoading ? "Updating..." : "Update Salts"}
          </button>
        </div>
      )}
    </div>
  );
};
